import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

/**
 * Reassign-stores dialog shown when an admin marks a dealer inactive or closed.
 * Purely presentational — parent owns state and submits the payload.
 *
 * Props:
 *  - open, onOpenChange
 *  - dealer            { first_name, last_name, id }        (selected dealer)
 *  - targetStatus      "inactive" | "closed"
 *  - locations         [{ id, name, address_line1, city, state }]
 *  - choices           { [location_id]: "unassign" | "remove" | "<other_dealer_id>" }
 *  - onChoiceChange    (location_id, value) => void
 *  - dealers           full dealer list (for reassignment target options)
 *  - submitting        boolean
 *  - onConfirm         () => void
 */
export function ReassignStoresDialog({
  open,
  onOpenChange,
  dealer,
  targetStatus,
  locations = [],
  choices = {},
  onChoiceChange,
  dealers = [],
  submitting,
  onConfirm,
}) {
  const defaultAction = targetStatus === "closed" ? "remove" : "unassign";
  const otherDealers = dealers.filter(
    (d) => d.id !== dealer?.id && d.status === "approved"
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">
            Reassign stores — marking {dealer?.first_name} {dealer?.last_name} as {targetStatus}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4" data-testid="reassign-dialog-body">
          <p className="text-sm text-slate-600">
            This dealer has <span className="font-medium">{locations.length}</span> store{locations.length === 1 ? "" : "s"} assigned. Decide what happens to each before {targetStatus === "closed" ? "closing" : "marking inactive"}:
          </p>
          <div className="border border-slate-200 rounded-lg divide-y max-h-[380px] overflow-y-auto">
            {locations.map((loc) => (
              <div
                key={loc.id}
                className="p-3 flex items-center gap-3"
                data-testid={`reassign-row-${loc.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-navy truncate">{loc.name}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {loc.address_line1 || loc.city}
                    {loc.city && loc.address_line1 ? `, ${loc.city}` : ""}
                    {loc.state ? `, ${loc.state}` : ""}
                  </p>
                </div>
                <Select
                  value={choices[loc.id] || defaultAction}
                  onValueChange={(v) => onChoiceChange(loc.id, v)}
                >
                  <SelectTrigger
                    className="w-[230px] h-8 text-xs"
                    data-testid={`reassign-choice-${loc.id}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassign">Keep on site, unassign only</SelectItem>
                    {targetStatus === "closed" && (
                      <SelectItem value="remove">Remove from site</SelectItem>
                    )}
                    <div className="px-2 py-1 text-[10px] font-medium text-slate-400 uppercase">
                      Reassign to
                    </div>
                    {otherDealers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.first_name} {d.last_name} · {d.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="reassign-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              disabled={submitting}
              className="bg-navy hover:bg-navy/90 text-white"
              data-testid="reassign-confirm"
            >
              {submitting ? "Applying..." : `Confirm & mark ${targetStatus}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
